import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

interface Emotion {
  name: string;
  emoji: string;
  color: string;
  note: string;
  noteName: string;
}

interface PianoActivityProps {
  onComplete: (selectedEmotion: string) => void;
}

const PianoActivity: React.FC<PianoActivityProps> = ({ onComplete }) => {
  const { t } = useLanguage();
  const [selectedEmotion, setSelectedEmotion] = useState<string | null>(null);
  const [pressedEmotion, setPressedEmotion] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const emotions: Emotion[] = [
    { name: '기쁨', emoji: '😊', color: '#FFD700', note: 'C', noteName: '도' },
    { name: '행복', emoji: '😄', color: '#FF8C00', note: 'D', noteName: '레' },
    { name: '설렘', emoji: '😍', color: '#FF69B4', note: 'E', noteName: '미' },
    { name: '평온', emoji: '😌', color: '#87CEEB', note: 'F', noteName: '파' },
    { name: '감사', emoji: '🙏', color: '#90EE90', note: 'G', noteName: '솔' },
    { name: '위로', emoji: '🤗', color: '#DDA0DD', note: 'A', noteName: '라' },
    { name: '실망', emoji: '😔', color: '#AAAAAA', note: 'B', noteName: '시' },
    { name: '불안', emoji: '😰', color: '#708090', note: 'C2', noteName: '높은도' },
    { name: '슬픔', emoji: '😢', color: '#4169E1', note: 'D2', noteName: '높은레' },
    { name: '화남', emoji: '😡', color: '#DC143C', note: 'E2', noteName: '높은미' },
  ];

  const playNote = (emotionName: string) => {
    const emotion = emotions.find(e => e.name === emotionName);
    if (!emotion) return;

    console.log(`피아노 음계 ${emotion.note} (${emotion.noteName}) 재생`);
    
    // 실제 오디오 파일이 없으므로 시스템 사운드나 햅틱 피드백으로 대체
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    
    // 간단한 사운드 효과 (실제로는 오디오 파일을 재생해야 함)
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // 음계에 따른 주파수 설정
    const frequencies: { [key: string]: number } = {
      'C': 261.63, 'D': 293.66, 'E': 329.63, 'F': 349.23,
      'G': 392.00, 'A': 440.00, 'B': 493.88,
      'C2': 523.25, 'D2': 587.33, 'E2': 659.25
    };
    
    oscillator.frequency.setValueAtTime(frequencies[emotion.note] || 261.63, audioContext.currentTime);
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  };

  const handleEmotionClick = (emotionName: string) => {
    setSelectedEmotion(emotionName);
    setPressedEmotion(emotionName);
    playNote(emotionName);
    
    // 눌림 효과 해제
    setTimeout(() => {
      setPressedEmotion(null);
    }, 200);
  };

  const handleSave = () => {
    if (selectedEmotion) {
      onComplete(selectedEmotion);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-md mx-auto px-6 py-4">
          <div className="flex items-center">
            <button
              onClick={() => window.history.back()}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="flex-1 text-center text-xl font-bold text-green-500">
              감정 표현
            </h1>
            <div className="w-10"></div>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-6 py-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            피아노로 감정 표현하기
          </h2>
          <p className="text-gray-600">
            감정을 선택하여 피아노 음계로 표현해보세요
          </p>
        </div>

        {/* 피아노 아이콘 */}
        <div className="text-center mb-8">
          <div className="w-24 h-28 mx-auto bg-white rounded-lg shadow-sm flex items-center justify-center">
            <span className="text-4xl">🎹</span>
          </div>
        </div>

        {/* 감정 선택 그리드 */}
        <div className="grid grid-cols-5 gap-2 mb-8">
          {emotions.map((emotion) => {
            const isSelected = selectedEmotion === emotion.name;
            const isPressed = pressedEmotion === emotion.name;
            
            return (
              <button
                key={emotion.name}
                onClick={() => handleEmotionClick(emotion.name)}
                className={`
                  aspect-square rounded-2xl flex flex-col items-center justify-center transition-all duration-150
                  ${isSelected ? 'ring-2 ring-green-500 ring-offset-2' : ''}
                  ${isPressed ? 'scale-95 shadow-lg' : 'hover:scale-105'}
                `}
                style={{
                  backgroundColor: emotion.color,
                  transform: isPressed ? 'scale(0.95)' : 'scale(1)',
                  boxShadow: isPressed ? `0 4px 12px ${emotion.color}80` : 'none'
                }}
              >
                <span className="text-lg mb-1">{emotion.emoji}</span>
                <span className="text-xs font-semibold text-white text-center leading-tight">
                  {emotion.name}
                </span>
              </button>
            );
          })}
        </div>

        {/* 저장 버튼 */}
        <button
          onClick={handleSave}
          disabled={!selectedEmotion}
          className={`
            w-full btn-primary px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed
            ${selectedEmotion 
              ? 'bg-green-500 hover:bg-green-600' 
              : 'bg-gray-300 cursor-not-allowed'
            }
          `}
        >
          <div className="flex items-center justify-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            저장하기g
          </div>
        </button>
      </div>
    </div>
  );
};

export default PianoActivity; 